import { html } from '@polymer/polymer';
import '@polymer/paper-item/paper-item.js';

import { KatapultEditorTemplate } from './katapult-editor-template';
import '../katapult-elements/katapult-button.js';
import '../../elements/katapult-color-picker/katapult-color-picker-button.js';
import '../../elements/katapult-color-picker/katapult-color-picker-dialog.js';
import '../katapult-tooltip/katapult-tooltip.js';

import { ColorTranslator } from 'colortranslator';
import { Path } from '../../modules/Path.js';
import '../../elements/katapult-elements/katapult-dialog.js';
import { Parallel } from '../../modules/Parallel.js';

import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';
import pluralize from 'pluralize-esm';
import { KatapultDialog } from '../../elements/katapult-elements/katapult-dialog.js';

/**
 * @typedef {Object} LayerConfig
 * @property {string} url The spatial reference identifier.
 * @property {string} name The name of the layer.
 * @property {string} [api_layer_group] The API layer group.
 * @property {object} options The options for the layer.
 * @property {number} [options.srid] The spatial reference identifier.
 * @property {object} [options.symbology] The symbology options for the layer.
 * @property {string} options.symbology.type The symbology type for the layer.
 * @property {string} [options.symbology.style] The style for the layer.
 * @property {object} options.symbology.vectorOptions The vector options for the layer.
 * @property {number} [options.symbology.vectorOptions.fillOpacity] The fill opacity for the layer.
 * @property {string} [options.symbology.vectorOptions.fillColor] The fill color for the layer.
 * @property {number} [options.symbology.vectorOptions.strokeOpacity] The stroke opacity for the layer.
 * @property {string} [options.symbology.vectorOptions.strokeColor] The stroke color for the layer.
 * @property {number} [options.symbology.vectorOptions.strokeWeight] The stroke weight for the layer.
 * @property {string} [options.fields] The fields for the layer.
 */

/**
 * Fetches the layer configuration from the provided URL, and, optionally, applies it a supplied layer configuration.
 * @param {string} url The URL from which to fetch the layer configuration.
 * @returns {Promise<LayerConfig|LayerConfig[]>} The layer configuration.
 */
async function FetchLayerConfigFromURL(url) {
  // Build the url to the json data (only works for ArcGIS).
  const layerData = await fetch(`${url}?f=pjson`)
    .then((res) => res.json())
    .catch(() => {
      throw new Error('Unable to fetch JSON data from the provided URL.');
    });
  if (layerData == null) throw new Error('No data found at the provided URL.');

  // If this url is in fact a group of layers, fetch and return the layer configurations for each.
  if (Array.isArray(layerData.layers)) {
    const layerConfigs = await Parallel.map(layerData.layers, async (layer) => {
      const layerUrl = Path.join([url, layer.id.toString()], '/');
      const layerConfig = await FetchLayerConfigFromURL(layerUrl);
      return layerConfig;
    });
    return layerConfigs.flat();
  }

  const { name = null } = layerData;

  /** @type {LayerConfig} */
  const layerConfig = { url, name, options: {} };

  // Get and set the srid from the data.
  const srid = layerData.extent?.spatialReference?.latestWkid;
  if (srid) layerConfig.options.srid = srid;

  // Initialize symbology type.
  Path.set(layerConfig, 'options.symbology.type', 'single');

  // Get and set the icon ("style").
  const style = layerData.drawingInfo?.renderer?.symbol?.style;
  if (style) Path.set(layerConfig, 'options.symbology.style', style);

  // Get and set the fill color and opacity.
  const fillColorArray = layerData.drawingInfo?.renderer?.symbol?.color ?? [];
  const fillColorResult = SplitColor(fillColorArray);
  if (fillColorResult?.opacity) Path.set(layerConfig.options, 'symbology.vectorOptions.fillOpacity', fillColorResult.opacity);
  if (fillColorResult?.color) Path.set(layerConfig.options, 'symbology.vectorOptions.fillColor', fillColorResult.color);

  // Get and set the stroke color and opacity.
  const strokeColorArray = layerData.drawingInfo?.renderer?.symbol?.outline?.color ?? [];
  const strokeColorResult = SplitColor(strokeColorArray);
  if (strokeColorResult?.opacity) Path.set(layerConfig.options, 'symbology.vectorOptions.strokeOpacity', strokeColorResult.opacity);
  if (strokeColorResult?.color) Path.set(layerConfig.options, 'symbology.vectorOptions.strokeColor', strokeColorResult.color);

  // Get and set the stroke weight.
  const strokeWeight = layerData.drawingInfo?.renderer?.symbol?.outline?.width;
  if (strokeWeight) Path.set(layerConfig.options, 'symbology.vectorOptions.strokeWeight', strokeWeight);

  if (Array.isArray(layerData.fields)) {
    const fieldNames = layerData.fields.map((field) => field.name);
    layerConfig.options.fields = fieldNames.join(', ');
  }

  return layerConfig;
}

function SplitColor(colorArray) {
  if (colorArray?.length == 0) return;
  // compute the opacity value
  let alphaColor = colorArray.pop() ?? 0.8; // 0.8 is the default stroke opacity
  let opacity = alphaColor / 255;
  // compute the hex value for the color
  let rgbString = `rgb(${colorArray[0]}, ${colorArray[1]}, ${colorArray[2]})`;
  let colorTranslator = new ColorTranslator(rgbString);
  let color = colorTranslator.HEX;
  if (color) color = color.toLowerCase();
  // return the opacity and color
  return { opacity, color };
}

class KatapultApiLayerEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        [animate-invisible] {
          transition: opacity 0.3s;
        }
        [invisible] {
          opacity: 0;
          pointer-events: none;
        }
        [modified] {
          --paper-input-container-input: {
            font-weight: bold;
          };
        }
        .styleLabel {
          margin-right: 1em;
        }
        .labelStylePairing {
          display: flex;
          justify-content: flex-start;
          align-items: center;
        }
        .styleRow {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          margin: 1em;
          padding: 1em;
        }
        .styleInfoCell {
          max-width: 200px;
          overflow: visible;
          height: fit-content;
        }
        .styleInput {
          max-width: 3em;
          border-bottom: 1px solid black;
        }
        #urlInput {
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.06);
          background: var(--paper-grey-50);
          padding-left: 1em;
        }
        .strikethrough {
          position: absolute;
          height: 2px;
          width: calc(100% - 20px);
          margin: -1px 10px 0 10px;
          top: 50%;
          background-color: var(--primary-color);
        }
        .blueOutlineButton {
          border: 1px solid var(--secondary-color);
          color: var(--secondary-color);
        }
        sl-spinner {
          font-size: 1.5rem;
          --indicator-color: var(--secondary-color);
        }
      </style>
      <katapult-dialog id="manageLayerGroupsDialog" icon="edit" title="Manage Layer Groups">
        <paper-table outline rounded dividers overflow-hidden>
          <template is="dom-repeat" items="[[apiLayerGroups]]" as="group">
            <paper-row>
              <paper-cell>[[group.value]]</paper-cell>
              <paper-cell icon-button>
                <katapult-button iconOnly noBorder icon="delete" on-click="deleteLayerGroup"></katapult-button>
              </paper-cell>
            </paper-row>
          </template>
          <paper-row>
            <paper-cell>
              <paper-input label="New Layer Group" no-label-float></paper-input>
              <katapult-button icon="add_circle_outline" iconOnly noBorder on-click="addLayerGroup"></katapult-button>
            </paper-cell>
          </paper-row>
        </paper-table>
        <katapult-button slot="buttons" dialog-dismiss>Close</katapult-button>
      </katapult-dialog>
      <katapult-dialog id="addFieldDialog" title="Add Field">
        <paper-input type="text" value="{{newFieldName}}" label="Field Name" no-label-float></paper-input>
        <div slot="buttons">
          <katapult-button on-click="addField" color="var(--secondary-color)">Add Field</katapult-button>
          <katapult-button dialog-dismiss>Cancel</katapult-button>
        </div>
      </katapult-dialog>
      <katapult-color-picker-dialog id="colorPickerDialog"></katapult-color-picker-dialog>
      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <!-- Step for setting the url -->
          <template is="dom-if" if="[[equal(step.id, 'url')]]" restamp>
            <paper-table style="width: 100%;" slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    id="urlInput"
                    type="url"
                    label="Enter the URL for the API"
                    value="[[_itemData.url]]"
                    on-change="setProperty"
                    data-path="url"
                    no-label-float
                  ></paper-input>
                </paper-cell>
                <katapult-collapse horizontal opened="[[_loadingLayerConfig]]">
                  <paper-cell icon>
                    <sl-spinner></sl-spinner>
                  </paper-cell>
                </katapult-collapse>
                <template is="dom-if" if="[[_loadingLayerConfigError]]">
                  <paper-cell style="color: var(--sl-color-danger-500); gap: var(--sl-spacing-small);">
                    <katapult-icon icon="error"></katapult-icon>
                    <div>No data was found at this URL</div>
                  </paper-cell>
                </template>
                <paper-cell animate-invisible invisible$="[[!_editingSingleLayer]]">
                  <katapult-button color="var(--secondary-color)" callback="applyURLData">Auto-fill Layer Details</katapult-button>
                  <iron-icon icon="help" style="margin-left: 1em">
                    <katapult-tooltip style="max-width:300px" position="right">
                      <div>
                        Clicking this button will <strong>overwrite</strong> all of the current data with the data retrieved with the
                        current API Endpoint URL. You can use this to quickly setup the layer with the proper base data, then you can make
                        any other changes as needed.
                      </div>
                    </katapult-tooltip>
                  </iron-icon>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Review multiple layers -->
          <template is="dom-if" if="[[equal(step.id, 'review_layers')]]" restamp>
            <div style="flex-grow: 1; display: flex; flex-direction: column; gap: var(--sl-spacing-small);" slot="body">
              <div>The following layers were found:</div>
              <paper-table rounded outline dividers overflow-hidden>
                <paper-row light-grey short>
                  <paper-cell>Layer</paper-cell>
                </paper-row>
                <template is="dom-if" if="[[_layerConfig.length]]" restamp>
                  <template is="dom-repeat" items="[[_layerConfig]]" as="layerConfig">
                    <paper-row expand>
                      <paper-cell>[[layerConfig.name]]</paper-cell>
                    </paper-row>
                  </template>
                </template>
                <template is="dom-if" if="[[!_layerConfig.length]]" restamp>
                  <paper-row>
                    <paper-cell style="justify-content: center;">No Layers 😔</paper-cell>
                  </paper-row>
                </template>
              </paper-table>
            </div>
          </template>
          <!-- Step for setting the Layer Name -->
          <template is="dom-if" if="[[equal(step.id, 'name')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    type="text"
                    value="[[_itemData.name]]"
                    label="Layer Name"
                    no-label-float
                    on-change="setProperty"
                    data-path="name"
                  ></paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
          <!-- Step for setting the Layer Group -->
          <template is="dom-if" if="[[equal(step.id, 'layer_group')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    items="[[apiLayerGroups]]"
                    label-path="value"
                    value-path="_id"
                    label="Select a Layer Group"
                    value="[[_itemData.api_layer_group]]"
                    no-label-float
                    on-value-changed="setProperty"
                    data-path="api_layer_group"
                  >
                    <paper-item style="gap: var(--sl-spacing-small); text-transform: unset;" on-click="manageLayerGroups">
                      <div>Manage Layer Groups</div>
                      <katapult-icon icon="edit"></katapult-icon>
                    </paper-item>
                  </katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="skipSettingGroup"
              >Skip</katapult-button
            >
          </template>
          <!-- Step for setting the srid -->
          <template is="dom-if" if="[[equal(step.id, 'srid')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    type="text"
                    value="[[_itemData.options.srid]]"
                    label="SRID"
                    no-label-float
                    on-value-changed="setProperty"
                    data-path="options.srid"
                  ></paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <iron-icon icon="help" slot="header" style="margin-left: 1em">
              <katapult-tooltip style="max-width:300px" position="right">
                <div>
                  The SRID is the Spatial Reference Identifier. This is the coordinate system that the layer is in. If you are unsure of the
                  SRID, you can usually find it under the
                  <strong> Extent </strong> section in parentheses.
                </div>
              </katapult-tooltip>
            </iron-icon>
          </template>
          <!-- Step for setting the unique field -->
          <template is="dom-if" if="[[equal(step.id, 'unique_field')]]" restamp>
            <paper-table light-grey style="width: 400px;" rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    type="text"
                    value="[[_itemData.options.uniqueField]]"
                    label="Unique Field"
                    no-label-float
                    on-value-changed="setProperty"
                    data-path="options.uniqueField"
                  ></paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <iron-icon icon="help" slot="header" style="margin-left: 1em">
              <katapult-tooltip style="max-width:300px" position="right">
                <div>
                  The unique field helps to determine which features are already on the map to increase performance. This will default to
                  OBJECTID.
                </div>
              </katapult-tooltip>
            </iron-icon>
          </template>
          <!-- Step for setting the layer styles -->
          <template is="dom-if" if="[[equal(step.id, 'layer_styles')]]" restamp>
            <paper-table outline rounded slot="body" style="padding: 1em 0em">
              <paper-row class="styleRow">
                <paper-cell class="styleInfoCell" shrink column align-start>
                  <span>Use Source Styles</span>
                  <span style="font-size: 10pt;" italic>Fetch style options directly from the source (overrides custom settings).</span>
                </paper-cell>
                <paper-cell>
                  <paper-checkbox
                    checked="[[_itemData.options.use_source_styles]]"
                    on-checked-changed="setProperty"
                    data-path="options.use_source_styles"
                  ></paper-checkbox>
                </paper-cell>
              </paper-row>
              <paper-row class="styleRow">
                <paper-cell class="styleInfoCell" shrink column align-start>
                  <span>Fill</span>
                  <span style="font-size: 10pt;" italic> This affects how the polygon for the layer will be filled </span>
                </paper-cell>
                <paper-cell shrink>
                  <div class="labelStylePairing">
                    <span class="styleLabel">Opacity:</span>
                    <paper-input
                      type="text"
                      style="max-width: 3em; border-bottom: 3px solid black;"
                      value="[[_itemData.options.symbology.vectorOptions.fillOpacity]]"
                      no-label-float
                      data-path="options.symbology.vectorOptions.fillOpacity"
                      on-value-changed="setProperty"
                    ></paper-input>
                  </div>
                </paper-cell>
                <paper-cell>
                  <div class="labelStylePairing">
                    <span class="styleLabel">Color:</span>
                    <katapult-color-picker-button
                      color="[[_itemData.options.symbology.vectorOptions.fillColor]]"
                      on-color-changed="setProperty"
                      data-path="options.symbology.vectorOptions.fillColor"
                    ></katapult-color-picker-button>
                  </div>
                </paper-cell>
              </paper-row>
              <paper-row class="styleRow">
                <paper-cell class="styleInfoCell" shrink column align-start>
                  <span>Stroke</span>
                  <span style="font-size: 10pt;" italic> This effects how the outline on the polygon for the layer will look. </span>
                </paper-cell>
                <paper-cell shrink>
                  <div class="labelStylePairing">
                    <span class="styleLabel">Opacity:</span>
                    <paper-input
                      class="styleInput"
                      type="text"
                      value="[[_itemData.options.symbology.vectorOptions.strokeOpacity]]"
                      no-label-float
                      data-path="options.symbology.vectorOptions.strokeOpacity"
                      on-value-changed="setProperty"
                    ></paper-input>
                  </div>
                </paper-cell>
                <paper-cell shrink>
                  <div class="labelStylePairing">
                    <span class="styleLabel">Weight:</span>
                    <paper-input
                      class="styleInput"
                      type="text"
                      min="0"
                      value="[[_itemData.options.symbology.vectorOptions.strokeWeight]]"
                      no-label-float
                      data-path="options.symbology.vectorOptions.strokeWeight"
                      on-value-changed="setProperty"
                    ></paper-input>
                  </div>
                </paper-cell>
                <paper-cell>
                  <div class="labelStylePairing">
                    <span class="styleLabel">Color:</span>
                    <katapult-color-picker-button
                      color="[[_itemData.options.symbology.vectorOptions.strokeColor]]"
                      on-color-changed="setProperty"
                      data-path="options.symbology.vectorOptions.strokeColor"
                    ></katapult-color-picker-button>
                  </div>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <!-- Step for setting the fields -->
          <template is="dom-if" if="[[equal(step.id, 'fields')]]" restamp>
            <iron-icon icon="help" slot="header" style="margin-right: 1em">
              <katapult-tooltip style="max-width:300px" position="right">
                <div>
                  Fields are the properties that show up when you click on the polygon on the map. You can add or remove fields in this
                  section. If you want to add all of the fields from the layer, you can use the * character, which is what is set by
                  default.
                </div>
              </katapult-tooltip>
            </iron-icon>
            <template is="dom-if" if="[[removingFields]]">
              <katapult-button
                slot="header"
                color="var(--secondary-color)"
                size="18"
                on-click="confirmRemovingFields"
                icon="remove_circle_outline"
                style="margin-right: 8px;"
                >Confirm Removing Fields</katapult-button
              >
              <katapult-button slot="header" size="18" style="height:34px; margin-right: 8px;" on-click="toggleRemovingFields"
                >Cancel</katapult-button
              >
            </template>
            <template is="dom-if" if="[[!removingFields]]">
              <template is="dom-if" if="[[showRemoveFields(fieldsArray.*)]]">
                <katapult-button
                  slot="header"
                  class="blueOutlineButton"
                  size="18"
                  on-click="toggleRemovingFields"
                  icon="remove_circle_outline"
                  style="margin-right: 8px;"
                  >Remove Fields</katapult-button
                >
              </template>
            </template>
            <katapult-button color="var(--secondary-color)" slot="header" size="18" on-click="openAddFieldDialog" icon="add_circle_outline"
              >Add Field</katapult-button
            >
            <paper-table light-grey rounded outline slot="body" style="overflow:hidden;">
              <paper-row header secondary-color>
                <paper-cell>Field From API Endpoint</paper-cell>
              </paper-row>
              <template is="dom-repeat" items="{{fieldsArray}}">
                <paper-row>
                  <template is="dom-if" if="[[item.remove]]">
                    <div class="strikethrough"></div>
                  </template>
                  <paper-cell>[[item.name]]</paper-cell>
                  <template is="dom-if" if="[[removingFields]]">
                    <paper-cell icon-button
                      ><katapult-button iconOnly noBorder icon="delete" on-click="toggleRemoveField"></katapult-button
                    ></paper-cell>
                  </template>
                </paper-row>
              </template>
            </paper-table>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      appliedURL: {
        type: Boolean,
        value: null
      },
      removingFields: {
        type: Boolean,
        value: false
      },
      fieldsArray: {
        type: Array,
        value: () => []
      }
    };
  }

  static get observers() {
    return [
      'urlChanged(_itemData.url)',
      'setupDefaultLayerData(currentIndex)',
      'fieldsStringChanged(_itemData.options.fields)',
      'fieldsArrayChanged(fieldsArray.*)',
      'setupFirebaseListeners(modelSpaceDetails.path)'
    ];
  }

  constructor() {
    super();
    this.updateComputedLabelConfig();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupFirebaseListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroyFirebaseListeners();
  }

  setupFirebaseListeners() {
    this.destroyFirebaseListeners();

    const { path } = this.modelSpaceDetails ?? {};
    if (!path) return;

    const apiLayerGroupsRef = (this.apiLayerGroupsRef = globalThis.firebase.database().ref(Path.join([path, 'map_api_layer_groups'], '/')));
    const apiLayerGroupsValueCallback = (s) =>
      (this.apiLayerGroups = Object.entries(s.val() ?? {}).map(([_id, value]) => ({ _id, ...value })));
    apiLayerGroupsRef.on('value', apiLayerGroupsValueCallback);
    this._disconnectApiLayerGroupsListener = () => {
      apiLayerGroupsRef.off('value', apiLayerGroupsValueCallback);
      this.apiLayerGroupsRef = null;
      this._disconnectApiLayerGroupsListener = null;
    };
  }

  destroyFirebaseListeners() {
    this._disconnectApiLayerGroupsListener?.();
  }

  manageLayerGroups() {
    const dialog = /** @type {KatapultDialog | undefined} */ (this.$.manageLayerGroupsDialog);
    if (dialog == null) throw new Error('Unable to find manage layer groups dialog');
    dialog.open();
  }

  async addLayerGroup() {
    const dialog = /** @type {KatapultDialog | undefined} */ (this.$.manageLayerGroupsDialog);
    if (dialog == null) throw new Error('Unable to find manage layer groups dialog');
    const newGroupNameInput = dialog.querySelector('paper-input');
    if (newGroupNameInput == null) throw new Error('Unable to find new group name input');
    const newGroupName = newGroupNameInput?.value?.trim();
    if (!newGroupName?.length) return;
    if (this.apiLayerGroups?.find((x) => x.value === newGroupName)) return;
    await this.apiLayerGroupsRef.push({ value: newGroupName });
    newGroupNameInput.value = '';
  }

  async deleteLayerGroup(e) {
    const { _id } = e?.model?.group ?? {};
    if (!_id) return;
    const confirmed = await KatapultDialog.confirm({
      text: 'Are you sure you want to delete this layer group?',
      dialog: {
        title: 'Delete Layer Group?',
        color: 'var(--sl-color-danger-500)',
        icon: 'delete'
      },
      confirmButton: {
        text: 'Delete',
        color: 'var(--sl-color-danger-500)'
      }
    }).confirmed;
    if (!confirmed) return;
    await this.apiLayerGroupsRef.child(_id).remove();
    // clear the layeg group if it is the one currently selected
    if (this._itemData.api_layer_group === _id) this.deepSet('_itemData.api_layer_group', null);
  }

  ready() {
    super.ready();
    this.labelAllowAllCharacters = true;
    // Otherwise, set normal steps
    this.setSteps(this.getNormalSteps());
  }

  skipSettingGroup() {
    this.nextStep();
    this.checkSteps();
  }

  // Returns an array of "normal" steps for the editor
  getNormalSteps() {
    // An ordered list of all possible steps.
    return [
      {
        id: 'url',
        title: 'API Endpoint URL',
        trigger: () => this._layerConfig != null
      },
      {
        id: 'review_layers',
        title: 'Review Layers',
        trigger: () => true,
        if: () => this._editingMultipleLayers === true
      },
      {
        id: 'name',
        title: 'Layer Name',
        trigger: () => this.get('_itemData.name') != null,
        if: () => this._editingSingleLayer === true
      },
      {
        id: 'layer_group',
        title: 'Layer Group',
        trigger: () => this.get('_itemData.api_layer_group') != null
      },
      {
        id: 'srid',
        title: 'SRID',
        trigger: () => this.get('_itemData.options.srid') != null,
        if: () => this._editingSingleLayer === true
      },
      {
        id: 'unique_field',
        title: 'Unique Field',
        trigger: () => this.get('_itemData.options.uniqueField') != null,
        if: () => this._editingSingleLayer === true
      },
      {
        id: 'layer_styles',
        title: 'Layer Styles',
        trigger: () => this.get('_itemData.options.uniqueField') != null,
        if: () => this._editingSingleLayer === true
      },
      {
        id: 'fields',
        title: 'Fields',
        trigger: () => this._itemData?.options?.fields,
        if: () => this._editingSingleLayer === true
      }
    ];
  }

  or(a, b) {
    return a || b;
  }

  updateComputedLabelConfig() {
    this.computedLabel =
      this._editingMultipleLayers === true
        ? {}
        : {
            dependencies: [{ path: 'name', optional: () => this._editingMultipleLayers }],
            compute: (name) => name ?? `Untitled ${this.itemTypeTitles.title}`
          };
  }

  urlInputChangeHandler(e) {
    const { value } = e.currentTarget;
    this.set('_itemData.url', value);
  }

  async urlChanged(url) {
    this._layerConfig = null;
    this._loadingLayerConfigError = null;
    this._editingSingleLayer = false;
    this._editingMultipleLayers = false;

    if (url) {
      this._loadingLayerConfig = true;
      const layerConfig = await FetchLayerConfigFromURL(url)
        .catch((err) => {
          this._loadingLayerConfigError = err;
        })
        .finally(() => {
          this._loadingLayerConfig = false;
        });
      if (layerConfig != null) {
        const multipleLayers = Array.isArray(layerConfig);
        this._editingSingleLayer = !multipleLayers;
        this._editingMultipleLayers = multipleLayers;
        this._layerConfig = layerConfig;
      }
      this.createItemButtonTitle = this._editingMultipleLayers ? 'Create API Layers' : null;
    }

    // Manually trigger some update methods that don't observe property changes outside of _itemData.
    this.checkSteps();
    this.updateCanSaveItem();
    this.updateComputedLabelConfig();
    this.updateComputedLabel();
    if (this._editingMultipleLayers) this.setLabel(pluralize(this.itemTypeTitles.title));
  }

  itemIsValid() {
    return !this._loadingLayerConfig && !this._loadingLayerConfigError && this._layerConfig != null;
  }

  setupDefaultLayerData() {
    const { _itemData, currentIndex } = this;
    if (_itemData == null) return;
    switch (currentIndex) {
      case 1: {
        const srid = this._itemData.options?.srid;
        if (srid == null) this.deepSet('_itemData.options.srid', 4326);
        break;
      }
      case 2: {
        const uniqueField = this._itemData.options?.uniqueField;
        if (uniqueField == null) this.deepSet('_itemData.options.uniqueField', 'OBJECTID');
        break;
      }
      case 3: {
        const vectorOptions = this._itemData.options?.symbology?.vectorOptions ?? {};
        const secondaryColor = globalThis.config?.firebaseData?.palette?.secondaryColor;
        if (vectorOptions.fillColor == null) this.deepSet('_itemData.options.symbology.vectorOptions.fillColor', secondaryColor);
        if (vectorOptions.fillOpacity == null) this.deepSet('_itemData.options.symbology.vectorOptions.fillOpacity', 0.3);
        if (vectorOptions.strokeColor == null) this.deepSet('_itemData.options.symbology.vectorOptions.strokeColor', secondaryColor);
        if (vectorOptions.strokeOpacity == null) this.deepSet('_itemData.options.symbology.vectorOptions.strokeOpacity', 0.8);
        if (vectorOptions.strokeWeight == null) this.deepSet('_itemData.options.symbology.vectorOptions.strokeWeight', 4);
        break;
      }
      case 4: {
        const fields = this._itemData.options?.fields;
        if (fields == null) this.deepSet('_itemData.options.fields', '*');
        break;
      }
    }
  }

  /**
   * Cleans up properties of the layer configuration prior to saving.
   * @param {LayerConfig} layerConfig
   * @param {Object} [options]
   * @param {boolean} [options.skipNameCollision=false] Whether to skip appending a number to the name if it is a duplicate.
   * @returns {LayerConfig} The sanitized layer configuration.
   */
  sanitizeLayerConfigPriorToSave(layerConfig, { skipNameCollision = false } = {}) {
    if (layerConfig == null) throw new Error('Layer configuration is required');
    // Parse all of the fields that are meant to be numbers.
    const pathsWithInts = ['fillOpacity', 'strokeOpacity', 'strokeWeight'];
    pathsWithInts.forEach((prop) => {
      const currentVal = layerConfig.options?.symbology?.vectorOptions[prop];
      if (currentVal == null) return;
      const floatVal = parseFloat(currentVal);
      Path.set(layerConfig, `options.symbology.vectorOptions.${prop}`, floatVal);
    });
    if (!skipNameCollision) {
      // Append number to name if it is a duplicate.
      const { name } = layerConfig;
      const layers = Object.values(this.categoryData ?? {});
      const suffixRegex = /\(\d+\)$/; // Matches a number in parentheses at the end of a string.
      /** @type {{nameWithoutSuffix: string, numericalSuffix: number}[]} */
      const conflictingNames = layers
        .map((layer) => {
          const { 0: suffix, index: suffixIndex } = layer.name.match(suffixRegex) ?? {};
          /** @type {string} */
          const nameWithoutSuffix = layer.name.slice(0, suffixIndex).trim();
          if (nameWithoutSuffix !== name) return null;
          const numericalSuffix = parseInt(suffix?.slice(1, -1)) || 0;
          return { nameWithoutSuffix, numericalSuffix };
        })
        .filter((x) => x != null);
      const conflictingNameWithLargestSuffix = conflictingNames.sort((a, b) => b.numericalSuffix - a.numericalSuffix)[0];
      if (conflictingNameWithLargestSuffix != null) {
        const { nameWithoutSuffix, numericalSuffix } = conflictingNameWithLargestSuffix;
        const newName = `${nameWithoutSuffix} (${numericalSuffix + 1})`;
        layerConfig.name = newName;
      }
    }
    return layerConfig;
  }

  async save() {
    // We are overriding the save method to handle saving multiple layers at once.
    // super.save() automatically passes _itemData to _save().
    // Here we call _save() manually, passing in each layer's configuration.
    if (this._editingMultipleLayers) {
      const layerConfigs = this._layerConfig;
      if (!Array.isArray(layerConfigs)) throw new Error('_layerConfig is not an array');
      await Parallel.map(layerConfigs, async (layerConfig) => {
        const itemData = { ...layerConfig, $key: GeneratePushID() };
        if (this._itemData.api_layer_group) itemData.api_layer_group = this._itemData.api_layer_group;
        this.sanitizeLayerConfigPriorToSave(itemData);
        await this._save(itemData);
      });
      this.dispatchEvent(new CustomEvent('clear-selected-item-forcefully'));
    } else if (this._editingSingleLayer) {
      this.sanitizeLayerConfigPriorToSave(this._itemData, { skipNameCollision: true });
      await super.save();
    } else {
      throw new Error('Unexpected layer configuration state');
    }
  }

  saved() {
    // The editor template's saved() method re-sets the editing item to be the saved item.
    // I assume this is to begin editing an item which has just been created.
    // We do not want this to happen for each layer when multiple are being created at once, so we skip it.
    if (this._editingMultipleLayers) return;
    super.saved();
  }

  setProperty(e) {
    // get the changed option's id and value
    let optionValue = e.detail.value ?? e.currentTarget.value;
    let optionPath = e.currentTarget.dataset?.path;
    if (optionValue == null || optionPath == null) return;
    // apply the toLowerCase() method to all color values
    let colorValues = ['fillColor', 'strokeColor'];
    if (colorValues.some((path) => optionPath.includes(path))) optionValue = optionValue.toLowerCase();
    // set the value at the path
    this.deepSet(Path.join(['_itemData', optionPath]), optionValue);
  }

  async applyURLData() {
    this.set('appliedURL', null);
    const layerConfig = await FetchLayerConfigFromURL(this._itemData.url).finally(() => {
      this.set('appliedURL', false);
    });
    if (Array.isArray(layerConfig)) throw new Error('Unexpected layer configuration state');
    const flattenedConfig = Path.flattenObject(layerConfig);
    for (const [path, value] of Object.entries(flattenedConfig)) {
      const fullPath = Path.join(['_itemData', path]);

      // Special case for setting the uniqueField option based on the first field in the fields string
      if (fullPath === '_itemData.options.fields') {
        // get the first field from the fields string
        const firstField = value?.split?.(',')[0]?.trim();
        if (firstField) {
          this.set('_itemData.options.uniqueField', firstField);
          this.notifyPath('_itemData.options.uniqueField');
        }
      }

      Path.set(this, fullPath, value);
      this.notifyPath(fullPath);
    }
    this.set('appliedURL', true);
  }

  fieldsStringChanged() {
    // get the fields
    let fields = this._itemData?.options?.fields;
    if (!fields || typeof fields != 'string') {
      this.fieldsArray = [];
      return;
    }
    // convert the fields string to an array that can be manipulated
    let fieldsArray = fields.split(', ').map((field) => {
      return { name: field, remove: false };
    });
    this.fieldsArray = fieldsArray;
  }

  fieldsArrayChanged() {
    // get the fields array
    let fieldsArray = this.fieldsArray;
    if (!fieldsArray) return;

    // convert the fields array to a string
    let fieldsString = fieldsArray.map((field) => field.name).join(', ');
    if (fieldsString) this.set('_itemData.options.fields', fieldsString);
  }

  toggleRemovingFields() {
    this.removingFields = !this.removingFields;
    this.fieldsArray.forEach((item, i) => {
      if (item.remove) {
        this.set(`fieldsArray.${i}.remove`, false);
      }
    });
  }

  toggleRemoveField(e) {
    this.set(`fieldsArray.${e.model.index}.remove`, !this.get(`fieldsArray.${e.model.index}.remove`));
  }

  async confirmRemovingFields(e) {
    let fields = structuredClone(this.fieldsArray);
    let fieldsToRemove = fields.filter((x) => x.remove).map((x) => x.name);
    if (fieldsToRemove.length > 0) {
      // remove the fields from the item
      while (fieldsToRemove.length > 0) {
        let field = fieldsToRemove.pop();
        let fieldIndex = fields.findIndex((x) => x.name === field);
        fields.splice(fieldIndex, 1);
      }
      this.set('fieldsArray', [...fields]);
    }
    // reset the fields table
    this.toggleRemovingFields();
  }

  openAddFieldDialog() {
    let addFieldDialog = this.shadowRoot?.querySelector('#addFieldDialog');
    if (!addFieldDialog) return;
    addFieldDialog.open();
  }

  addField() {
    if (!this.newFieldName) return;
    // add the field name to the array
    this.fieldsArray.push({ name: this.newFieldName, remove: false });
    this.set('fieldsArray', [...this.fieldsArray]);
    // clear the new field name
    this.newFieldName = '';
    // close the dialog
    let addFieldDialog = this.shadowRoot?.querySelector('#addFieldDialog');
    if (!addFieldDialog) return;
    addFieldDialog.close();
  }

  showRemoveFields() {
    return this.fieldsArray.length > 0;
  }
}

customElements.define('katapult-api-layer-editor', KatapultApiLayerEditor);
